import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase.config';
import { FAQ, FAQCategory } from '../types';

// Collection references
const faqsCollection = collection(db, 'faqs');
const categoriesCollection = collection(db, 'faq_categories');

// Get all active FAQs
export const getFAQs = async (): Promise<FAQ[]> => {
  try {
    // Query semplificata senza orderBy per evitare problemi di indice
    const q = query(
      faqsCollection,
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    const faqs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FAQ[];
    
    // Ordinamento lato client
    return faqs.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    throw new Error('Errore nel caricamento delle FAQ');
  }
};

// Get FAQs by category
export const getFAQsByCategory = async (category: string): Promise<FAQ[]> => {
  try {
    // Query semplificata senza orderBy per evitare problemi di indice
    const q = query(
      faqsCollection,
      where('category', '==', category),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    const faqs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FAQ[];
    
    // Ordinamento lato client
    return faqs.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching FAQs by category:', error);
    throw new Error('Errore nel caricamento delle FAQ per categoria');
  }
};

// Get all FAQs (admin only)
export const getAllFAQs = async (): Promise<FAQ[]> => {
  try {
    // Query semplificata senza orderBy
    const q = query(faqsCollection);
    const querySnapshot = await getDocs(q);
    const faqs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FAQ[];
    
    // Ordinamento lato client
    return faqs.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching all FAQs:', error);
    throw new Error('Errore nel caricamento di tutte le FAQ');
  }
};

// Get single FAQ
export const getFAQ = async (id: string): Promise<FAQ | null> => {
  try {
    const docRef = doc(faqsCollection, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as FAQ;
    }
    return null;
  } catch (error) {
    console.error('Error fetching FAQ:', error);
    throw new Error('Errore nel caricamento della FAQ');
  }
};

// Create new FAQ
export const createFAQ = async (faqData: Omit<FAQ, 'id' | 'createdAt' | 'updatedAt'>): Promise<FAQ> => {
  try {
    const now = Timestamp.now();
    const newFAQ = {
      ...faqData,
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(faqsCollection, newFAQ);
    return {
      id: docRef.id,
      ...newFAQ
    } as FAQ;
  } catch (error) {
    console.error('Error creating FAQ:', error);
    throw new Error('Errore nella creazione della FAQ');
  }
};

// Update FAQ
export const updateFAQ = async (id: string, updates: Partial<FAQ>): Promise<void> => {
  try {
    const docRef = doc(faqsCollection, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating FAQ:', error);
    throw new Error('Errore nell\'aggiornamento della FAQ');
  }
};

// Delete FAQ
export const deleteFAQ = async (id: string): Promise<void> => {
  try {
    const docRef = doc(faqsCollection, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting FAQ:', error);
    throw new Error('Errore nell\'eliminazione della FAQ');
  }
};

// Get FAQ categories
export const getFAQCategories = async (): Promise<FAQCategory[]> => {
  try {
    // Query semplificata senza orderBy
    const q = query(categoriesCollection);
    const querySnapshot = await getDocs(q);
    const categories = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FAQCategory[];
    
    // Ordinamento lato client
    return categories.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching FAQ categories:', error);
    throw new Error('Errore nel caricamento delle categorie FAQ');
  }
};

// Initialize default FAQ categories
export const initializeFAQCategories = async (): Promise<void> => {
  try {
    const defaultCategories: Omit<FAQCategory, 'id'>[] = [
      {
        name: 'Generale',
        description: 'Domande generali sull\'utilizzo della piattaforma',
        icon: 'HelpCircle',
        order: 1
      },
      {
        name: 'Candidature',
        description: 'Come gestire le candidature di lavoro',
        icon: 'Briefcase',
        order: 2
      },
      {
        name: 'Colloqui',
        description: 'Gestione colloqui e appuntamenti',
        icon: 'Calendar',
        order: 3
      },
      {
        name: 'Moderatori',
        description: 'Funzionalità per i moderatori',
        icon: 'Users',
        order: 4
      },
      {
        name: 'Amministrazione',
        description: 'Funzionalità per gli amministratori',
        icon: 'Shield',
        order: 5
      }
    ];

    const batch = writeBatch(db);
    
    for (const category of defaultCategories) {
      const docRef = doc(categoriesCollection);
      batch.set(docRef, category);
    }
    
    await batch.commit();
  } catch (error) {
    console.error('Error initializing FAQ categories:', error);
    throw new Error('Errore nell\'inizializzazione delle categorie FAQ');
  }
};

// Check for duplicate FAQs
export const checkDuplicateFAQs = async (): Promise<{
  duplicates: Array<{ group: FAQ[], reason: string }>;
  totalFAQs: number;
  uniqueFAQs: number;
}> => {
  try {
    const allFAQs = await getAllFAQs();
    const duplicates: Array<{ group: FAQ[], reason: string }> = [];
    
    // Raggruppa FAQ per domanda (case insensitive)
    const questionGroups = new Map<string, FAQ[]>();
    allFAQs.forEach(faq => {
      const normalizedQuestion = faq.question.toLowerCase().trim();
      if (!questionGroups.has(normalizedQuestion)) {
        questionGroups.set(normalizedQuestion, []);
      }
      questionGroups.get(normalizedQuestion)!.push(faq);
    });
    
    // Trova gruppi con più di una FAQ
    questionGroups.forEach((group, question) => {
      if (group.length > 1) {
        duplicates.push({
          group,
          reason: `Domanda duplicata: "${group[0].question}"`
        });
      }
    });
    
    // Raggruppa FAQ per categoria e ordine (potenziali doppioni di inizializzazione)
    const categoryOrderGroups = new Map<string, FAQ[]>();
    allFAQs.forEach(faq => {
      const key = `${faq.category}-${faq.order}`;
      if (!categoryOrderGroups.has(key)) {
        categoryOrderGroups.set(key, []);
      }
      categoryOrderGroups.get(key)!.push(faq);
    });
    
    // Trova gruppi con più di una FAQ per stessa categoria/ordine
    categoryOrderGroups.forEach((group, key) => {
      if (group.length > 1) {
        const [category, order] = key.split('-');
        duplicates.push({
          group,
          reason: `Stessa categoria (${category}) e ordine (${order}): ${group.length} FAQ`
        });
      }
    });
    
    return {
      duplicates,
      totalFAQs: allFAQs.length,
      uniqueFAQs: allFAQs.length - duplicates.reduce((acc, dup) => acc + dup.group.length - 1, 0)
    };
  } catch (error) {
    console.error('Error checking duplicate FAQs:', error);
    throw new Error('Errore nel controllo dei doppioni FAQ');
  }
};

// Remove duplicate FAQs
export const removeDuplicateFAQs = async (): Promise<{
  removed: number;
  kept: number;
  details: Array<{ removed: FAQ[], kept: FAQ, reason: string }>;
}> => {
  try {
    const { duplicates } = await checkDuplicateFAQs();
    const batch = writeBatch(db);
    const details: Array<{ removed: FAQ[], kept: FAQ, reason: string }> = [];
    let totalRemoved = 0;
    let totalKept = 0;
    
    for (const duplicate of duplicates) {
      // Mantieni la FAQ più recente (ultima creata) e rimuovi le altre
      const sortedGroup = duplicate.group.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Più recente prima
      });
      
      const kept = sortedGroup[0];
      const removed = sortedGroup.slice(1);
      
      // Rimuovi le FAQ duplicate
      for (const faqToRemove of removed) {
        const docRef = doc(faqsCollection, faqToRemove.id);
        batch.delete(docRef);
        totalRemoved++;
      }
      
      totalKept++;
      details.push({
        removed,
        kept,
        reason: duplicate.reason
      });
    }
    
    if (totalRemoved > 0) {
      await batch.commit();
    }
    
    return {
      removed: totalRemoved,
      kept: totalKept,
      details
    };
  } catch (error) {
    console.error('Error removing duplicate FAQs:', error);
    throw new Error('Errore nella rimozione dei doppioni FAQ');
  }
};

// Clean up FAQ data (remove duplicates and fix ordering)
export const cleanupFAQs = async (): Promise<{
  duplicatesRemoved: number;
  orderingFixed: number;
  totalFAQs: number;
}> => {
  try {
    console.log('🧹 Inizio pulizia FAQ...');
    
    // Rimuovi doppioni
    const duplicateResult = await removeDuplicateFAQs();
    console.log(`✅ Rimossi ${duplicateResult.removed} doppioni`);
    
    // Ottieni tutte le FAQ rimanenti
    const allFAQs = await getAllFAQs();
    
    // Riorganizza l'ordine per categoria
    const categoryGroups = new Map<string, FAQ[]>();
    allFAQs.forEach(faq => {
      if (!categoryGroups.has(faq.category)) {
        categoryGroups.set(faq.category, []);
      }
      categoryGroups.get(faq.category)!.push(faq);
    });
    
    const batch = writeBatch(db);
    let orderingFixed = 0;
    
    // Riorganizza l'ordine per ogni categoria
    categoryGroups.forEach((faqs, category) => {
      const sortedFAQs = faqs.sort((a, b) => {
        // Prima per ordine, poi per data di creazione
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return aTime - bTime;
      });
      
      // Aggiorna l'ordine se necessario
      sortedFAQs.forEach((faq, index) => {
        const newOrder = index + 1;
        if (faq.order !== newOrder) {
          const docRef = doc(faqsCollection, faq.id);
          batch.update(docRef, { order: newOrder });
          orderingFixed++;
        }
      });
    });
    
    if (orderingFixed > 0) {
      await batch.commit();
    }
    
    console.log(`✅ Sistemato l'ordine di ${orderingFixed} FAQ`);
    console.log(`📊 Totale FAQ dopo pulizia: ${allFAQs.length}`);
    
    return {
      duplicatesRemoved: duplicateResult.removed,
      orderingFixed,
      totalFAQs: allFAQs.length
    };
  } catch (error) {
    console.error('Error cleaning up FAQs:', error);
    throw new Error('Errore nella pulizia delle FAQ');
  }
}; 